import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery, useTheme, IconButton, Tooltip, TextField, InputAdornment, CircularProgress } from '@mui/material';
import { Edit as EditIcon, DeleteOutline as DeleteIcon, Check as CheckIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import AIChatContainer from '../AIChat/AIChatContainer';
import ChatList from './ChatList';
import TokenCounter from './TokenCounter';
import CreditPurchaseModal from './CreditPurchaseModal';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { AI_INITIAL_CREDITS_COUPLE, AI_INITIAL_CREDITS_SUPPLIER, getEstimatedCost } from '../../utils/aiTokenConstants';
import { chatTheme } from '../../theme/chatTheme';
import useThemeStyles from '../../theme/useThemeStyles';

// Import BusinessContext safely
let BusinessContext;
try {
  const BusinessContextModule = require('../../context/BusinessContext');
  BusinessContext = BusinessContextModule?.BusinessContext;
  
  if (!BusinessContext || typeof BusinessContext !== 'object' || !BusinessContext.Provider) {
    BusinessContext = React.createContext(null);
  }
} catch (error) {
  BusinessContext = React.createContext(null);
}

const useBusinessSafe = () => {
  const context = React.useContext(BusinessContext);
  return context || { selectedBusiness: null, loading: true };
};

const LumiChatPage = () => {
  const theme = useTheme();
  const styles = useThemeStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, wedding } = useAuth();
  const { selectedBusiness, loading: businessLoading } = useBusinessSafe();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [isNewChatInput, setIsNewChatInput] = useState(false); // Mobile: show chat input when "New Chat" clicked
  const [activeChatTitle, setActiveChatTitle] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState(null);
  const editInputRef = useRef(null);

  // Check for purchase success/cancel from Stripe redirect
  useEffect(() => {
    if (searchParams.get('purchaseSuccess')) {
      loadCredits();
      setSearchParams({}, { replace: true }); // Clear params
    } else if (searchParams.get('purchaseCanceled')) {
      setSearchParams({}, { replace: true }); // Clear params
    }
  }, [searchParams, setSearchParams]);

  // Determine mode and IDs
  const mode = user?.role === 'supplier' ? 'supplier' : 'couple';
  const weddingId = wedding?._id;
  const businessId = selectedBusiness?.business?._id;

  const loadCredits = async () => {
    try {
      setLoadingCredits(true);
      const response = await api.get('/api/ai/credits');
      setCreditsInfo(response.data);
    } catch (error) {
      setCreditsInfo(null);
    } finally {
      setLoadingCredits(false);
    }
  };

  useEffect(() => {
    loadCredits();
  }, [weddingId, businessId]);


  const handlePurchaseSuccess = useCallback(() => {
    loadCredits();
  }, []);

  const handleSelectChat = useCallback((chatId) => {
    setSelectedChatId(chatId);
  }, []);

  const handleNewChat = useCallback(() => {
    setSelectedChatId(null);
  }, []);

  const handleChatCreated = useCallback((chatId) => {
    setSelectedChatId(chatId);
    setIsNewChatInput(false);
  }, []);

  const handleChatChanged = useCallback((chatId) => {
    setSelectedChatId(chatId);
    // Load chat title when chat changes
    if (chatId) {
      loadChatTitle(chatId);
    } else {
      setActiveChatTitle(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, []);

  const [chatListKey, setChatListKey] = useState(0); // Force ChatList to reload
  const [_chats, setChats] = useState([]); // Store chats to get title

  const loadChatTitle = async (chatId) => {
    try {
      const response = await api.get('/api/chat/conversations');
      const chat = response.data.find(c => c._id === chatId);
      if (chat) {
        setActiveChatTitle(chat.title || (mode === 'couple' ? 'Chat with Lumi' : 'Lumi Business Assistant'));
      }
    } catch (error) {}
  };

  // Auto-focus input when editing starts
  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingChatId]);

  const handleStartEdit = () => {
    if (selectedChatId) {
      setEditingChatId(selectedChatId);
      setEditingTitle(activeChatTitle || 'Untitled Chat');
    }
  };

  const handleCancelEdit = () => {
    setEditingChatId(null);
    setEditingTitle('');
  };

  const handleSaveTitle = async () => {
    if (!editingTitle.trim() || !selectedChatId) {
      setEditingTitle('');
      setEditingChatId(null);
      return;
    }

    try {
      setSavingTitle(true);
      await api.put(`/api/chat/conversations/${selectedChatId}`, {
        title: editingTitle.trim()
      });
      
      setActiveChatTitle(editingTitle.trim());
      setEditingChatId(null);
      setEditingTitle('');
      
      // Refresh chat list
      setChatListKey(prev => prev + 1);
    } catch (error) {
      alert('Failed to save title. Please try again.');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleDeleteChat = async () => {
    if (!selectedChatId) return;
    
    if (!window.confirm('Are you sure you want to archive this chat? You can start a new chat anytime.')) {
      return;
    }

    try {
      setDeletingChatId(selectedChatId);
      await api.delete(`/api/chat/conversations/${selectedChatId}`);
      setSelectedChatId(null);
      setActiveChatTitle(null);
      // Refresh chat list
      setChatListKey(prev => prev + 1);
    } catch (error) {
      alert('Failed to archive chat. Please try again.');
    } finally {
      setDeletingChatId(null);
    }
  };

  // Load chats list to get titles
  useEffect(() => {
    const loadChats = async () => {
      try {
        const response = await api.get('/api/chat/conversations');
        const aiChats = response.data.filter(chat => 
          chat.type === 'ai-couple' || chat.type === 'ai-supplier'
        );
        setChats(aiChats);
        if (selectedChatId) {
          const chat = aiChats.find(c => c._id === selectedChatId);
          if (chat) {
            setActiveChatTitle(chat.title || (mode === 'couple' ? 'Chat with Lumi' : 'Lumi Business Assistant'));
          }
        }
      } catch (error) {}
    };
    loadChats();
  }, [selectedChatId, mode]);

  const initialCredits = mode === 'couple' ? AI_INITIAL_CREDITS_COUPLE : AI_INITIAL_CREDITS_SUPPLIER;
  const _estimatedChatCost = getEstimatedCost('chat');
  const _estimatedTaskCost = getEstimatedCost('task');

  return (
    <Box
      sx={{
        display: 'flex',
        height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 120px)' },
        minHeight: { xs: '400px', sm: '500px', md: '600px' },
        maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 120px)' },
        borderRadius: { xs: '8px', sm: '12px', md: '16px' },
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Mobile: List page (layer 1) - full-width chat list with white bg, same as messages */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          backgroundColor: '#fff',
          overflow: 'auto',
          visibility: { xs: (selectedChatId || isNewChatInput) ? 'hidden' : 'visible', md: 'hidden' },
        }}
      >
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
          <Box sx={{ ...chatTheme.sidebar.header, width: '100%', mb: 2 }}>
            <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 2 }}>
              Lumi Assistant
            </Typography>
            <TokenCounter
              creditsInfo={creditsInfo}
              loading={loadingCredits}
              onPurchaseClick={() => setPurchaseModalOpen(true)}
              initialCredits={initialCredits}
              mode={mode}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', width: '100%' }}>
            <ChatList
              key={chatListKey}
              mode={mode}
              weddingId={weddingId}
              businessId={businessId}
              selectedChatId={selectedChatId}
              onSelectChat={(chatId) => {
                handleSelectChat(chatId);
                setIsNewChatInput(false);
                if (chatId) {
                  loadChatTitle(chatId);
                } else {
                  setActiveChatTitle(null);
                }
              }}
              onNewChat={() => {
                handleNewChat();
                setActiveChatTitle(null);
                if (isMobile) setIsNewChatInput(true);
              }}
              onChatUpdated={() => {
                setChatListKey(prev => prev + 1);
                if (selectedChatId) {
                  loadChatTitle(selectedChatId);
                }
              }}
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      {/* Desktop: Sidebar - Chat List */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '320px',
          flexShrink: 0,
          height: '100%',
        }}
      >
        <Box sx={chatTheme.sidebar.container}>
          <Box sx={{ ...chatTheme.sidebar.header, px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box component="img" src="/img/lumi.png" alt="Lumi" sx={{ width: 24, height: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Lumi Assistant
              </Typography>
            </Box>
            <TokenCounter
              creditsInfo={creditsInfo}
              loading={loadingCredits}
              onPurchaseClick={() => setPurchaseModalOpen(true)}
              initialCredits={initialCredits}
              mode={mode}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <ChatList
              key={chatListKey}
              mode={mode}
              weddingId={weddingId}
              businessId={businessId}
              selectedChatId={selectedChatId}
              onSelectChat={(chatId) => {
                handleSelectChat(chatId);
                if (chatId) loadChatTitle(chatId);
                else setActiveChatTitle(null);
              }}
              onNewChat={() => {
                handleNewChat();
                setActiveChatTitle(null);
              }}
              onChatUpdated={() => {
                setChatListKey(prev => prev + 1);
                if (selectedChatId) loadChatTitle(selectedChatId);
              }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Mobile: Chat page (layer 2) - overlays list when chat selected */}
      {/* Desktop: Main chat area - side by side with sidebar */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRadius: '16px',
          overflow: 'hidden',
          ml: { xs: 0, md: 2 },
          height: '100%',
          minHeight: 0,
          position: { xs: 'absolute', md: 'relative' },
          top: { xs: 0, md: 'auto' },
          left: { xs: 0, md: 'auto' },
          right: { xs: 0, md: 'auto' },
          bottom: { xs: 0, md: 'auto' },
          width: { xs: '100%', md: 'auto' },
          zIndex: { xs: 2, md: 'auto' },
          visibility: { xs: (selectedChatId || isNewChatInput) ? 'visible' : 'hidden', md: 'visible' },
          pointerEvents: { xs: (selectedChatId || isNewChatInput) ? 'auto' : 'none', md: 'auto' },
        }}
      >
        {/* Chat header - show when chat selected or new chat input (back button always available on mobile) */}
        {(selectedChatId || isNewChatInput) && (
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1 }}>
              {/* Back button on left for mobile - same as messages */}
              {isMobile && (
                <IconButton
                  onClick={() => {
                    setSelectedChatId(null);
                    setIsNewChatInput(false);
                  }}
                  aria-label="Back to chats"
                  size="small"
                  sx={{ mr: 1, flexShrink: 0 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              {editingChatId === selectedChatId && selectedChatId ? (
                <TextField
                  inputRef={editInputRef}
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveTitle();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                  size="small"
                  variant="standard"
                  sx={{
                    flex: 1,
                    '& .MuiInput-input': {
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#2C3E50',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleSaveTitle}
                          disabled={savingTitle}
                        >
                          {savingTitle ? (
                            <CircularProgress size={16} />
                          ) : (
                            <CheckIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={savingTitle}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <>
                  <Box sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#2C3E50', flex: 1 }}>
                    {activeChatTitle || (mode === 'couple' ? 'Chat with Lumi' : 'Lumi Business Assistant')}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {selectedChatId && (
                      <>
                        <Tooltip title="Edit title">
                          <IconButton
                            size="small"
                            onClick={handleStartEdit}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {deletingChatId === selectedChatId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Tooltip title="Archive chat">
                            <IconButton
                              size="small"
                              onClick={handleDeleteChat}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
        
        {/* Chat container */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <AIChatContainer
            mode={mode}
            weddingId={weddingId}
            businessId={businessId}
            businessLoading={mode === 'supplier' ? businessLoading : undefined}
            supplierMode={mode === 'supplier' ? 'business' : undefined}
            chatId={selectedChatId}
            onChatCreated={(chatId) => {
              handleChatCreated(chatId);
              loadChatTitle(chatId);
            }}
            onChatChanged={handleChatChanged}
            onChatUpdated={() => {
              setChatListKey(prev => prev + 1);
              if (selectedChatId) {
                loadChatTitle(selectedChatId);
              }
            }}
            onCreditsUpdated={() => {
              // Reload credits when a message is sent
              loadCredits();
            }}
            onInsufficientCredits={() => setPurchaseModalOpen(true)}
          />
        </Box>
      </Box>

      {/* Purchase Modal */}
      <CreditPurchaseModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </Box>
  );
};

export default LumiChatPage;
